
import axios from 'axios';
import { getBaseUrl } from './getBaseUrl';
import { getAuthToken, clearAuthToken } from './authStorage';

const inFlightGetRequests = new Map<string, Promise<any>>();

const getRequestKey = (url: string, config?: any) => {
  const params = config?.params ? JSON.stringify(config.params) : "";
  const fullUrl = `${config?.baseURL || ""}${url}`;
  return `${fullUrl}|${params}`;
};

const axiosInstance = axios.create({
  baseURL: getBaseUrl(),
  headers: {
    'Content-Type': 'application/json',
    Accept: '*/*',
  },
});

const originalGet = axiosInstance.get.bind(axiosInstance);
axiosInstance.get = ((url: string, config?: any) => {
  const key = getRequestKey(url, config);
  const existing = inFlightGetRequests.get(key);

  if (existing) {
    return existing;
  }

  const request = originalGet(url, config).finally(() => {
    inFlightGetRequests.delete(key);
  });

  inFlightGetRequests.set(key, request);
  return request;
}) as typeof axiosInstance.get;

// ✅ --- Request Interceptor (Auth) ---
axiosInstance.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) config.headers['Authorization'] = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

// Header set by the API's TenantFeatureFilter (axios lower-cases header names).
const FEATURE_DISABLED_HEADER = 'x-feature-disabled';
export const FEATURE_DISABLED_EVENT = 'cm:feature-module-disabled';
let lastFeatureDisabledAt = 0;

// ✅ --- Response Interceptor (401 handling + Caching) ---

axiosInstance.interceptors.response.use(
  async (response) => {
    // Cache only if the request has the cache header
    if (response.config.method === 'get' && response.config.headers['X-Cache-Response'] === 'true') {
      const key = `cache:${response.config.url}`;
      const cached = {
        data: response.data,
        timestamp: Date.now(),
      };
      localStorage.setItem(key, JSON.stringify(cached));
    }
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      clearAuthToken();
      window.location.href = '/login';
    }
    // 403 from a product module the company has switched off (TenantFeatureFilter):
    // the session's keys/modules are stale. The sidebar listens and re-fetches them,
    // so the module's menus and screens disappear. Throttled: a page can fire several.
    const disabledModule = error.response?.status === 403
      ? error.response?.headers?.[FEATURE_DISABLED_HEADER]
      : undefined;
    if (disabledModule && Date.now() - lastFeatureDisabledAt > 10000) {
      lastFeatureDisabledAt = Date.now();
      window.dispatchEvent(new CustomEvent(FEATURE_DISABLED_EVENT, { detail: disabledModule }));
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;
